<script setup>
import { onMounted, ref, computed } from "vue";
import { useStore } from "vuex";
import { isElementVisible } from "common/utils/ui/visibility";
import ScrollToElement from "common/utils/ui/scroll-to-element";
import CheckoutTotal from "../checkoutComponents/CheckoutTotal.vue";
import ArtworkLoadingMessage from "./ArtworkLoadingMessage.vue";
import ArtworkLoadedMessage from "./ArtworkLoadedMessage.vue";

const props = defineProps({
  forbiddenZone: {
    type: String,
    required: false,
    default: null
  }
});
const invisible = ref(false);

const store = useStore();
const imageUrl = computed(() => store.getters["artwork/artworkUrl"]);
const isUploadInProgress = computed(() => store.getters["loading/isUploadInProgress"]);
const artwork = computed(() => store.getters["artwork/artwork"]);
const isInactive = computed(() => isUploadInProgress.value || !artwork.value);

const handleScrollToAnchor = () => {
  if (artwork.value || isUploadInProgress.value) {
    ScrollToElement.scrollToElementBySelector("#artwork-scroll-anchor");
  } else {
    document.querySelector("#field-artwork-upload")?.click();
  }
};

onMounted(() => {
  const handleScroll = () => {
    const $forbiddenZone = props.forbiddenZone ? document.querySelector(props.forbiddenZone) : null;
    invisible.value = $forbiddenZone ? isElementVisible($forbiddenZone) : false;
  };
  window.addEventListener("scroll", handleScroll);
  handleScroll();
  return () => {
    window.removeEventListener("scroll", handleScroll);
  };
});
</script>

<template>
  <div
    class="artwork-sticky-box js-artwork-sticky-box"
    :class="{
      'artwork-sticky-box--invisible': invisible
    }"
    @click="handleScrollToAnchor"
  >
    <div v-if="isUploadInProgress" class="artwork-sticky-box__bg" />

    <div class="artwork-sticky-box__inner-bg">
      <div class="artwork-sticky-box__content-wrapper">
        <div v-if="isUploadInProgress" class="artwork-sticky-box__preview-wrapper">
          <img :src="imageUrl" alt="Artwork preview" class="artwork-sticky-box__preview" />
        </div>

        <div class="artwork-sticky-box__content">
          <ArtworkLoadingMessage v-if="isInactive" />
          <ArtworkLoadedMessage v-else />
        </div>

        <CheckoutTotal :inactive="isInactive" />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "frontend/init/pod/settings";

.artwork-sticky-box {
  position: sticky;
  top: 12px;
  z-index: 10;
  margin: 12px;
  background: $pod-brand-gradient;
  padding: 4px;
  color: $white;
  border-radius: 8px;
  box-shadow: 0 2px 7px 0 rgba(0, 0, 0, 0.3);
  overflow: hidden;

  &--invisible {
    opacity: 0;
    visibility: hidden;
  }

  &__bg {
    z-index: 0;
    position: absolute;
    inset: 0;
    background: $pod-brand-gradient;
    animation: moveBg 3s linear infinite;
  }

  &__inner-bg {
    background: $pod-gray-modern-900;
    border-radius: 4px;
    padding: 10px 13px;
    position: relative;
    z-index: 1;
  }

  &__content-wrapper {
    height: 40px;
    display: flex;
    align-items: center;
  }

  &__content {
    flex: 1;
  }

  &__preview-wrapper {
    flex: none;
    width: 25px;
    height: 39px;
    margin-right: 7px;
  }

  &__preview {
    width: 100%;
    height: 100%;
    object-fit: cover;
    font-size: 0;
  }
}

@keyframes moveBg {
  0% {
    transform: scale(0);
  }
  50% {
    transform: scale(3);
  }

  100% {
    transform: scale(0);
  }
}
</style>
